import React, { useMemo, useState } from 'react';
import { ArrowUpRightIcon, BarChart3Icon, BoxesIcon, ChevronDownIcon, CircleDollarSignIcon, PackageCheckIcon, TrendingUpIcon } from 'lucide-react';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { CategoryPerformance } from './CategoryPerformance';
import { SalesBySku } from './SalesBySku';
import { NisoloProductSales } from './NisoloProductSales';
import { useNisoloOrders } from '../hooks/useNisoloOrders';
const periods = ['Today', 'Yesterday', 'Month to date', 'Quarter to date', 'Year to date', 'All time'] as const;
type Period = (typeof periods)[number];
type ChartPoint = {
  label: string;
  sales: number;
  units: number;
};
type PeriodPerformance = {
  data: ChartPoint[];
  sales: number;
  units: number;
  averageUnitValue: string;
  sellThrough: string;
  comparison: string;
};
const performanceByPeriod: Record<Period, ChartPoint[]> = {
  Today: [{
    label: '9 AM',
    sales: 180,
    units: 6
  }, {
    label: '11 AM',
    sales: 360,
    units: 12
  }, {
    label: '1 PM',
    sales: 520,
    units: 17
  }, {
    label: '3 PM',
    sales: 760,
    units: 24
  }, {
    label: '5 PM',
    sales: 980,
    units: 31
  }],
  Yesterday: [{
    label: '9 AM',
    sales: 220,
    units: 8
  }, {
    label: '11 AM',
    sales: 390,
    units: 13
  }, {
    label: '1 PM',
    sales: 610,
    units: 20
  }, {
    label: '3 PM',
    sales: 840,
    units: 27
  }, {
    label: '5 PM',
    sales: 1120,
    units: 36
  }],
  'Month to date': [{
    label: 'May 1',
    sales: 820,
    units: 28
  }, {
    label: 'May 5',
    sales: 1280,
    units: 43
  }, {
    label: 'May 10',
    sales: 1840,
    units: 61
  }, {
    label: 'May 15',
    sales: 2390,
    units: 78
  }, {
    label: 'May 20',
    sales: 2970,
    units: 97
  }, {
    label: 'Today',
    sales: 3560,
    units: 116
  }],
  'Quarter to date': [{
    label: 'Apr',
    sales: 4380,
    units: 152
  }, {
    label: 'Late Apr',
    sales: 5820,
    units: 201
  }, {
    label: 'Early May',
    sales: 6840,
    units: 236
  }, {
    label: 'Mid May',
    sales: 7920,
    units: 276
  }, {
    label: 'Today',
    sales: 9410,
    units: 322
  }],
  'Year to date': [{
    label: 'Jan',
    sales: 3840,
    units: 132
  }, {
    label: 'Feb',
    sales: 5270,
    units: 182
  }, {
    label: 'Mar',
    sales: 7340,
    units: 254
  }, {
    label: 'Apr',
    sales: 8920,
    units: 309
  }, {
    label: 'May',
    sales: 11120,
    units: 386
  }, {
    label: 'Jun',
    sales: 13840,
    units: 481
  }],
  'All time': [{
    label: 'Jul',
    sales: 5210,
    units: 181
  }, {
    label: 'Aug',
    sales: 7460,
    units: 256
  }, {
    label: 'Sep',
    sales: 6280,
    units: 217
  }, {
    label: 'Oct',
    sales: 9560,
    units: 331
  }, {
    label: 'Nov',
    sales: 11980,
    units: 415
  }, {
    label: 'Dec',
    sales: 14680,
    units: 508
  }]
};
const eddyPerformanceByPeriod: Record<Period, PeriodPerformance> = {
  Today: {
    data: [{
      label: 'Today',
      sales: 0,
      units: 0
    }],
    sales: 0,
    units: 0,
    averageUnitValue: '$0.00',
    sellThrough: '0%',
    comparison: 'No sales recorded today'
  },
  Yesterday: {
    data: [{
      label: '12 AM',
      sales: 0,
      units: 0
    }, {
      label: '6 AM',
      sales: 0,
      units: 0
    }, {
      label: '12 PM',
      sales: 0,
      units: 0
    }, {
      label: '6 PM',
      sales: 0,
      units: 0
    }, {
      label: '12 AM',
      sales: 0,
      units: 0
    }],
    sales: 0,
    units: 0,
    averageUnitValue: '$0.00',
    sellThrough: '0%',
    comparison: 'No sales recorded yesterday'
  },
  'Month to date': {
    data: [{
      label: 'Jun 4',
      sales: 520,
      units: 4
    }, {
      label: 'Jun 7',
      sales: 380,
      units: 3
    }, {
      label: 'Jun 10',
      sales: 720,
      units: 6
    }, {
      label: 'Jun 14',
      sales: 180,
      units: 2
    }, {
      label: 'Jun 18',
      sales: 430,
      units: 4
    }, {
      label: 'Jun 22',
      sales: 150,
      units: 1
    }, {
      label: 'Jun 26',
      sales: 850,
      units: 7
    }, {
      label: 'Jun 30',
      sales: 1410,
      units: 11
    }, {
      label: 'Jul 3',
      sales: 160,
      units: 1
    }, {
      label: 'Jul 7',
      sales: 850,
      units: 7
    }, {
      label: 'Jul 10',
      sales: 920,
      units: 8
    }, {
      label: 'Jul 14',
      sales: 400,
      units: 3
    }, {
      label: 'Jul 17',
      sales: 780,
      units: 6
    }, {
      label: 'Jul 21',
      sales: 180,
      units: 2
    }, {
      label: 'Jul 26',
      sales: 300,
      units: 3
    }],
    sales: 8609,
    units: 68,
    averageUnitValue: '$126.60',
    sellThrough: '8%',
    comparison: 'Since last month'
  },
  'Quarter to date': {
    data: [{
      label: 'May',
      sales: 10870,
      units: 148
    }, {
      label: 'Jun',
      sales: 34210,
      units: 266
    }, {
      label: 'Jul',
      sales: 8609,
      units: 68
    }],
    sales: 53689,
    units: 482,
    averageUnitValue: '$111.39',
    sellThrough: '29%',
    comparison: 'Since last quarter'
  },
  'Year to date': {
    data: [{
      label: 'Dec',
      sales: 3200,
      units: 30
    }, {
      label: 'Jan',
      sales: 6800,
      units: 72
    }, {
      label: 'Feb',
      sales: 17700,
      units: 168
    }, {
      label: 'Mar',
      sales: 6500,
      units: 58
    }, {
      label: 'Apr',
      sales: 33000,
      units: 340
    }, {
      label: 'May',
      sales: 21900,
      units: 224
    }, {
      label: 'Jun',
      sales: 27800,
      units: 255
    }, {
      label: 'Jul',
      sales: 22400,
      units: 68
    }, {
      label: 'Aug',
      sales: 7500,
      units: 55
    }, {
      label: 'Sep',
      sales: 6100,
      units: 48
    }, {
      label: 'Oct',
      sales: 5800,
      units: 42
    }, {
      label: 'Nov',
      sales: 4800,
      units: 36
    }, {
      label: 'Dec',
      sales: 44000,
      units: 365
    }, {
      label: 'Jan',
      sales: 14000,
      units: 110
    }, {
      label: 'Feb',
      sales: 22500,
      units: 174
    }, {
      label: 'Mar',
      sales: 10800,
      units: 88
    }, {
      label: 'Apr',
      sales: 7300,
      units: 64
    }, {
      label: 'May',
      sales: 10600,
      units: 88
    }, {
      label: 'Jun',
      sales: 8609,
      units: 68
    }],
    sales: 118763,
    units: 1215,
    averageUnitValue: '$97.75',
    sellThrough: '46%',
    comparison: 'Since last year'
  },
  'All time': {
    data: [{
      label: 'Jun 2025',
      sales: 12000,
      units: 171
    }, {
      label: 'Jul 2025',
      sales: 22800,
      units: 287
    }, {
      label: 'Aug 2025',
      sales: 7600,
      units: 92
    }, {
      label: 'Sep 2025',
      sales: 5900,
      units: 70
    }, {
      label: 'Oct 2025',
      sales: 5400,
      units: 66
    }, {
      label: 'Nov 2025',
      sales: 6000,
      units: 77
    }, {
      label: 'Dec 2025',
      sales: 43800,
      units: 550
    }, {
      label: 'Jan 2026',
      sales: 14000,
      units: 184
    }, {
      label: 'Feb 2026',
      sales: 22500,
      units: 301
    }, {
      label: 'Mar 2026',
      sales: 10800,
      units: 143
    }, {
      label: 'Apr 2026',
      sales: 7300,
      units: 96
    }, {
      label: 'May 2026',
      sales: 10800,
      units: 142
    }, {
      label: 'Jun 2026',
      sales: 8000,
      units: 105
    }],
    sales: 325875,
    units: 4758,
    averageUnitValue: '$68.49',
    sellThrough: '74%',
    comparison: 'All time'
  }
};
const conditionRows = [{
  label: 'Rescue',
  units: '3,550',
  sales: '$276,357',
  aur: '$78',
  discount: '76%',
  className: 'bg-[#4F8A5B]'
}, {
  label: 'Damaged',
  units: '1,199',
  sales: '$48,547',
  aur: '$40',
  discount: '0%',
  className: 'bg-[#C75A47]'
}, {
  label: 'Resale',
  units: '9',
  sales: '$971',
  aur: '$108',
  discount: '0%',
  className: 'bg-[#4D9CC7]'
}];
type ChannelPerformance = {
  name: string;
  orders: string;
  units: string;
  sales: string;
  averageOrder: string;
  aur: string;
  share: string;
  icon: 'manymoons' | 'whatnot' | 'shopify' | 'poshmark' | 'prive' | 'shop';
};
const eddyChannelPerformance: ChannelPerformance[] = [{
  name: 'Manymoons',
  orders: '1,205',
  units: '1,647',
  sales: '$172,228',
  averageOrder: '$143',
  aur: '$105',
  share: '52.9%',
  icon: 'manymoons'
}, {
  name: 'Whatnot',
  orders: '1,201',
  units: '2,789',
  sales: '$113,718',
  averageOrder: '$95',
  aur: '$41',
  share: '34.9%',
  icon: 'whatnot'
}, {
  name: 'Collective',
  orders: '181',
  units: '227',
  sales: '$29,563',
  averageOrder: '$163',
  aur: '$130',
  share: '9.1%',
  icon: 'shopify'
}, {
  name: 'Circular Shop',
  orders: '36',
  units: '47',
  sales: '$7,327',
  averageOrder: '$204',
  aur: '$156',
  share: '2.2%',
  icon: 'manymoons'
}, {
  name: 'Poshmark',
  orders: '15',
  units: '17',
  sales: '$1,920',
  averageOrder: '$128',
  aur: '$113',
  share: '0.6%',
  icon: 'poshmark'
}, {
  name: 'Prive',
  orders: '4',
  units: '31',
  sales: '$1,118',
  averageOrder: '$280',
  aur: '$36',
  share: '0.3%',
  icon: 'prive'
}];
const channelIconFor = (name: string): ChannelPerformance['icon'] => {
  const normalized = name.trim().toLowerCase();
  // A channel literally named "shop" is the Shop app, not a storefront.
  if (normalized === 'shop' || normalized === 'shop app') return 'shop';
  if (normalized.includes('whatnot')) return 'whatnot';
  if (normalized.includes('poshmark')) return 'poshmark';
  if (normalized.includes('prive') || normalized.includes('privé')) return 'prive';
  if (normalized.includes('shopify') || normalized.includes('collective')) return 'shopify';
  return 'manymoons';
};
export function PerformanceDashboard({
  selectedOwner
}: {
  selectedOwner: string;
}) {
  const [period, setPeriod] = useState<Period>('Year to date');
  const [periodOpen, setPeriodOpen] = useState(false);
  const isNisolo = selectedOwner === 'Nisolo';
  const {
    data: orders,
    loading: ordersLoading,
    error: ordersError
  } = useNisoloOrders(isNisolo);
  const nisoloPerformance = useMemo(() => {
    if (!orders) return null;
    const points = period === 'Today' || period === 'Yesterday' || period === 'Month to date' ? orders.daily.slice(-30) : orders.monthly;
    const scoped = points.length > 0 ? points : orders.monthly;
    return {
      data: scoped.map(point => ({
        label: point.label,
        sales: point.sales,
        units: point.units
      })),
      sales: orders.sales,
      units: orders.units,
      averageUnitValue: orders.aur.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 2
      }),
      sellThrough: orders.msrpValue > 0 ? `${Math.round(orders.sales / orders.msrpValue * 100)}%` : '0%',
      comparison: `${orders.firstOrder} – ${orders.lastOrder}`
    };
  }, [orders, period]);
  const eddyPerformance = selectedOwner === 'Eddy' ? eddyPerformanceByPeriod[period] : null;
  const activePerformance = nisoloPerformance ?? eddyPerformance;
  const data = activePerformance?.data ?? performanceByPeriod[period];
  const fallbackTotals = useMemo(() => data[data.length - 1] ?? {
    sales: 0,
    units: 0
  }, [data]);
  const totals = activePerformance ?? {
    sales: fallbackTotals.sales,
    units: fallbackTotals.units,
    comparison: `${period} performance`
  };
  const channelPerformance: ChannelPerformance[] = isNisolo && orders ? orders.channels.map(channel => ({
    name: channel.label,
    orders: channel.orders.toLocaleString('en-US'),
    units: channel.units.toLocaleString('en-US'),
    sales: formatCurrency(channel.sales),
    averageOrder: formatCurrency(channel.orders > 0 ? channel.sales / channel.orders : 0),
    aur: formatCurrency(channel.units > 0 ? channel.sales / channel.units : 0),
    share: `${orders.sales > 0 ? (channel.sales / orders.sales * 100).toFixed(1) : '0.0'}%`,
    icon: channelIconFor(channel.label)
  })) : selectedOwner === 'Eddy' ? eddyChannelPerformance : [];
  const conditionPerformance = isNisolo && orders ? orders.conditions.map((condition, index) => ({
    label: condition.label,
    units: condition.units.toLocaleString('en-US'),
    sales: formatCurrency(condition.sales),
    aur: formatCurrency(condition.units > 0 ? condition.sales / condition.units : 0),
    discount: `${condition.sales + condition.discount > 0 ? Math.round(condition.discount / (condition.sales + condition.discount) * 100) : 0}%`,
    className: ['bg-[#4F8A5B]', 'bg-[#C75A47]', 'bg-[#4D9CC7]', 'bg-[#C9A04E]'][index % 4]
  })) : conditionRows;
  const summaryMetrics = activePerformance ? {
    averageUnitValue: activePerformance.averageUnitValue,
    sellThrough: activePerformance.sellThrough
  } : {
    averageUnitValue: '$0.00',
    sellThrough: '0%'
  };
  const summaryDetail = isNisolo && orders ? 'All recorded orders' : `For ${period.toLowerCase()}`;
  return <div className="mx-auto max-w-7xl px-5 py-10 sm:px-8 lg:py-14" aria-labelledby="performance-heading" data-id="element-2011" data-mp="3c0igjpmeyklh">
      <section className="flex flex-col gap-5 border-b border-ink/10 pb-8 sm:flex-row sm:items-end sm:justify-between" data-id="element-2012" data-mp="3715u35zgffjf">
        <div data-id="element-2013" data-mp="8drv5ncicqmq">
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#BB5E40]" data-id="element-2014" data-mp="8di6q4742rj5">
            <BarChart3Icon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-2015" data-mp="1xtcarfjt7slr" />
            Performance
          </span>
          <h1 id="performance-heading" className="mt-3 font-display text-4xl font-medium tracking-tight sm:text-5xl" data-id="element-2016" data-mp="22s5pfbau1pni">
            Recovery performance
          </h1>
          <p className="mt-3 max-w-2xl text-base leading-relaxed text-ink/65 sm:text-lg" data-id="element-2017" data-mp="1o1ezk8ssx0n1">
            {selectedOwner ? `A high-level view of ${selectedOwner}'s recovery performance across approved channels.` : 'Select an owner in the sidebar to focus this view on one recovery program.'}
          </p>
        </div>
        <span className="inline-flex w-fit items-center gap-2 rounded-full bg-forest/8 px-3 py-1.5 text-xs font-semibold text-forest" data-id="element-2018" data-mp="1j23hmwgp7p3v">
          <span className="h-1.5 w-1.5 rounded-full bg-forest" data-id="element-2019" data-mp="1a2yipp185tr6" />
          {selectedOwner || 'All brands'}
        </span>
      </section>

      <section className="mt-8 rounded-3xl border border-ink/10 bg-paper p-5 shadow-sm sm:p-7" aria-label="Sales performance chart" data-id="element-2020" data-mp="2zjhdawtpcppl">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between" data-id="element-2021" data-mp="sufh8fxvkce0">
          <div data-id="element-2022" data-mp="nvcljm3sbiip">
            <p className="text-xs font-semibold uppercase tracking-wider text-ink/50" data-id="element-2023" data-mp="nvupz60lp31p">Financial performance</p>
            <div className="mt-2 flex flex-wrap items-end gap-x-8 gap-y-4" data-id="element-2024" data-mp="12kh2z12r9ipr">
              <div data-id="element-2025" data-mp="xtsc9nywgpro">
                <p className="font-display text-4xl font-semibold tracking-tight text-ink sm:text-5xl" data-id="element-2026" data-mp="xt8se1povrb0">{formatCurrency(totals.sales)}</p>
                <p className="mt-1 text-sm text-ink/55" data-id="element-2027" data-mp="1bj2iencxkhmb">Total sales</p>
              </div>
              <div className="border-l border-ink/10 pl-5" data-id="element-2028" data-mp="16laqhtcacvi4">
                <p className="font-display text-3xl font-semibold tracking-tight text-ink sm:text-4xl" data-id="element-2029" data-mp="1ljmmryvqy28r">{totals.units.toLocaleString('en-US')}</p>
                <p className="mt-1 text-sm text-ink/55" data-id="element-2030" data-mp="1gkrve8jpr5d1">Total units sold</p>
              </div>
              <span className="inline-flex items-center gap-1 pb-1 text-sm font-semibold text-forest" data-id="element-2031" data-mp="3nazpcha8ry1n">
                <TrendingUpIcon className="h-4 w-4" data-id="element-2032" data-mp="3id7o2wnpizks" />
                {totals.comparison}
              </span>
            </div>
            <p className="mt-2 text-sm text-ink/55" data-id="element-2033" data-mp="29gssj7rvl8ey">Gross recovery value · {period.toLowerCase()}</p>
          </div>
          <div className="relative w-full sm:w-48" data-id="element-2034" data-mp="2jcvsq8362wz9">
            <button type="button" onClick={() => setPeriodOpen(isOpen => !isOpen)} aria-expanded={periodOpen} aria-controls="performance-periods" className="flex w-full items-center justify-between rounded-xl border border-ink/10 bg-cream px-3 py-2.5 text-left text-sm font-semibold text-ink transition-colors hover:border-[#BB5E40]/35 focus:outline-none focus:ring-2 focus:ring-[#BB5E40]/15" data-id="element-2035" data-mp="2jddvlnkc49g5">
              {period}
              <ChevronDownIcon className={`h-4 w-4 text-ink/45 transition-transform ${periodOpen ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-2036" data-mp="24gmv1ayxcroy" />
            </button>
            {periodOpen && <div id="performance-periods" role="listbox" aria-label="Performance period" className="absolute right-0 z-10 mt-2 w-full overflow-hidden rounded-xl border border-ink/10 bg-paper p-1.5 shadow-lg" data-id="element-2037" data-mp="1bxhkpivonypg">
                {periods.map(item => <button key={item} type="button" role="option" aria-selected={period === item} onClick={() => {
              setPeriod(item);
              setPeriodOpen(false);
            }} className={`flex w-full rounded-lg px-3 py-2 text-left text-sm font-medium transition-colors ${period === item ? 'bg-[#BB5E40] text-cream' : 'text-ink/70 hover:bg-cream hover:text-ink'}`} data-id="element-2038" data-mp="vebrc1bqfpbo">
                    {item}
                  </button>)}
              </div>}
          </div>
        </div>

        <div className="mt-7 h-64 sm:h-80" data-id="element-2039" data-mp="3txief2zo91f3">
          <ResponsiveContainer width="100%" height="100%" data-id="element-2040" data-mp="3tyth0zhds34m">
            <AreaChart data={data} margin={{
            top: 10,
            right: 8,
            left: -15,
            bottom: 0
          }} data-id="element-2041" data-mp="3k821ddxlpefr">
              <defs data-id="element-2042" data-mp="3p8zehc3r7iae">
                <linearGradient id="recovery-area" x1="0" y1="0" x2="0" y2="1" data-id="element-2043" data-mp="3p9iwestem6of">
                  <stop offset="0%" stopColor="#BB5E40" stopOpacity={0.34} data-id="element-2044" data-mp="3abyz882r1fst" />
                  <stop offset="95%" stopColor="#BB5E40" stopOpacity={0.02} data-id="element-2045" data-mp="3f98nvlibdnfl" />
                </linearGradient>
              </defs>
              <CartesianGrid vertical={false} stroke="#1C1A17" strokeOpacity={0.1} strokeDasharray="3 5" data-id="element-2046" data-mp="6v9t2n72w83o" />
              <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{
              fill: '#6D6861',
              fontSize: 12
            }} dy={10} data-id="element-2047" data-mp="bsicer3yqi6i" />
              <YAxis axisLine={false} tickLine={false} tick={{
              fill: '#6D6861',
              fontSize: 12
            }} tickFormatter={(value: number) => formatAxisCurrency(value)} data-id="element-2048" data-mp="3t7p2rzqbyk8t" />
              <Tooltip content={<ChartTooltip data-id="element-2050" data-mp="pr9ibq8o8xjk" />} cursor={{
              stroke: '#BB5E40',
              strokeOpacity: 0.25,
              strokeWidth: 1
            }} data-id="element-2049" data-mp="ps15qds47oyr" />
              <Area type="monotone" dataKey="sales" stroke="#BB5E40" strokeWidth={3} fill="url(#recovery-area)" activeDot={{
              r: 5,
              fill: '#BB5E40',
              stroke: '#F7F4EC',
              strokeWidth: 3
            }} data-id="element-2051" data-mp="ukuav2rhwzbd" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </section>

      <section className="mt-5 grid gap-5 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]" data-id="element-2052" data-mp="2oypzx4l6k9s3">
        <article className="min-w-0 rounded-2xl border border-ink/10 bg-paper p-5 sm:p-6" aria-labelledby="condition-heading" data-id="element-2053" data-mp="2k0es0zeg49ci">
          <div className="flex items-center gap-2" data-id="element-2054" data-mp="1wbm1xjki8vpt">
            <PackageCheckIcon className="h-4 w-4 text-[#BB5E40]" aria-hidden="true" data-id="element-2055" data-mp="1rc98gfz4oqe9" />
            <h2 id="condition-heading" className="text-base font-semibold" data-id="element-2056" data-mp="25bse924gvpne">Sales by condition</h2>
          </div>
          <div className="mt-5 -mx-2 overflow-x-auto px-2" data-id="element-2057" data-mp="21af8st5adoh0">
            <table className="w-full min-w-[470px] text-left text-xs" data-id="element-2058" data-mp="21c9sy58qhck1">
              <thead className="border-b border-ink/10 text-ink/50" data-id="element-2059" data-mp="3gpmce43bwceu">
                <tr data-id="element-2060" data-mp="3cpvhppaolc51">
                  <th scope="col" className="pb-2 font-semibold" data-id="element-2061" data-mp="3coany1w6xx88">Condition</th>
                  <th scope="col" className="pb-2 text-right font-semibold" data-id="element-2062" data-mp="1a36tq3h2p8te">Units sold</th>
                  <th scope="col" className="pb-2 text-right font-semibold" data-id="element-2063" data-mp="1f18f4v1z1k81">Total sales</th>
                  <th scope="col" className="pb-2 text-right font-semibold" data-id="element-2064" data-mp="1k14kaxmenhp6">AUR</th>
                  <th scope="col" className="pb-2 text-right font-semibold" data-id="element-2065" data-mp="1jz08zum6fgev">Avg discount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink/10" data-id="element-2066" data-mp="1sty2b2t8kv6q">
                {conditionPerformance.map(condition => <tr key={condition.label} data-id="element-2067" data-mp="1xqy7zcm6bvxb">
                    <th scope="row" className="py-3 text-left font-medium" data-id="element-2068" data-mp="22rcid1b8zhyi">
                      <span className="inline-flex items-center gap-2" data-id="element-2069" data-mp="22s45rouoy9dp">
                        <span className={`h-2.5 w-2.5 rounded-full ${condition.className}`} data-id="element-2070" data-mp="27p49vu7zd226" />
                        {condition.label}
                      </span>
                    </th>
                    <td className="py-3 text-right tabular-nums text-ink/70" data-id="element-2071" data-mp="ddfq9eqt0bbg">{condition.units}</td>
                    <td className="py-3 text-right font-semibold tabular-nums text-ink" data-id="element-2072" data-mp="2qnpl9kbjgiru">{condition.sales}</td>
                    <td className="py-3 text-right tabular-nums text-ink/70" data-id="element-2073" data-mp="2lv5wrsbbc389">{condition.aur}</td>
                    <td className="py-3 text-right tabular-nums text-ink/70" data-id="element-2074" data-mp="2hv5iq8s4lu1q">{condition.discount}</td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </article>

        <article className="min-w-0 rounded-2xl border border-ink/10 bg-paper p-5 sm:p-6" aria-labelledby="channel-heading" data-id="element-2075" data-mp="39l0a0azjntt1">
          <div className="flex items-center gap-2" data-id="element-2076" data-mp="35i3wgyov2nvy">
            <CircleDollarSignIcon className="h-4 w-4 text-[#BB5E40]" aria-hidden="true" data-id="element-2077" data-mp="30jso6se7slfu" />
            <h2 id="channel-heading" className="text-base font-semibold" data-id="element-2078" data-mp="2vmk58pl8sddj">Channel performance</h2>
          </div>
          <div className="mt-5 -mx-2 overflow-x-auto px-2" data-id="element-2079" data-mp="1p6hsdysisehb">
            {channelPerformance.length > 0 ? <table className="w-full min-w-[700px] text-left text-xs" data-id="element-2080" data-mp="1k50t27pop02u">
                <thead className="border-b border-ink/10 text-ink/50" data-id="element-2081" data-mp="1vtyyg6795b8z">
                  <tr data-id="element-2082" data-mp="1r3a9oymyisdj">
                    <th scope="col" className="pb-2 font-semibold" data-id="element-2083" data-mp="1r2qon920fnva">Channel</th>
                    <th scope="col" className="pb-2 text-right font-semibold" data-id="element-2084" data-mp="1m3nvalve7635">Orders</th>
                    <th scope="col" className="pb-2 text-right font-semibold" data-id="element-2085" data-mp="1h4ujcxvr7n53">Units</th>
                    <th scope="col" className="pb-2 text-right font-semibold" data-id="element-2086" data-mp="1h2zwhe0x9kyd">Total sales</th>
                    <th scope="col" className="pb-2 text-right font-semibold" data-id="element-2087" data-mp="2eqib35h9lhaa">Avg order</th>
                    <th scope="col" className="pb-2 text-right font-semibold" data-id="element-2088" data-mp="29rf77od0th3w">AUR</th>
                    <th scope="col" className="pb-2 text-right font-semibold" data-id="element-2089" data-mp="24su9bvl8r9im">% of total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-ink/10" data-id="element-2090" data-mp="tf8g9zc9gnrt">
                  {channelPerformance.map(channel => <tr key={channel.name} data-id="element-2091" data-mp="ofngxnt6idek">
                      <th scope="row" className="py-3 text-left font-medium text-ink" data-id="element-2092" data-mp="oeuayp21nrxh">
                        <span className="flex items-center gap-2.5" data-id="element-2093" data-mp="3053hjcrefijf">
                          <ChannelMark type={channel.icon} data-id="element-2094" data-mp="351tye2b12i5w" />
                          {channel.name}
                        </span>
                      </th>
                      <td className="py-3 text-right tabular-nums text-ink/70" data-id="element-2095" data-mp="39zvfihow87eq">{channel.orders}</td>
                      <td className="py-3 text-right tabular-nums text-ink/70" data-id="element-2096" data-mp="3eu82h1pkw0v8">{channel.units}</td>
                      <td className="py-3 text-right font-semibold tabular-nums text-ink" data-id="element-2097" data-mp="3jtucl2a410zt">{channel.sales}</td>
                      <td className="py-3 text-right tabular-nums text-ink/70" data-id="element-2098" data-mp="3ss6cjuwv9b4x">{channel.averageOrder}</td>
                      <td className="py-3 text-right tabular-nums text-ink/70" data-id="element-2099" data-mp="1lmtxtksggw2">{channel.aur}</td>
                      <td className="py-3 text-right font-semibold tabular-nums text-[#BB5E40]" data-id="element-2100" data-mp="1yeev498apmyx">{channel.share}</td>
                    </tr>)}
                </tbody>
              </table> : <div className="py-8 text-center text-sm text-ink/55" data-id="element-2101" data-mp="1vbs9uo1pmacp">Select an owner to view channel performance.</div>}
          </div>
        </article>
      </section>

      <section className="mt-5 grid gap-5 sm:grid-cols-3" aria-label="Performance summary" data-id="element-2102" data-mp="25agfsogf6z01">
        <SummaryCard icon={BoxesIcon} label="Units sold" value={totals.units.toLocaleString('en-US')} detail={summaryDetail} data-id="element-2103" data-mp="20bn0qrlhizxr" />
        <SummaryCard icon={CircleDollarSignIcon} label="Average unit value" value={summaryMetrics.averageUnitValue} detail={summaryDetail} data-id="element-2104" data-mp="2eamn1sliz2qr" />
        <SummaryCard icon={ArrowUpRightIcon} label={isNisolo ? 'Recovery vs MSRP' : 'Sell-through'} value={summaryMetrics.sellThrough} detail={summaryDetail} data-id="element-2105" data-mp="2a9koldzfmupu" />
      </section>

      {isNisolo ? <>
          {ordersLoading && <p className="mt-5 rounded-2xl border border-ink/10 bg-paper px-5 py-10 text-center text-sm text-ink/55" data-id="element-2106" data-mp="2xyvrb10cez6m">Loading Nisolo order history…</p>}
          {ordersError && !ordersLoading && <p className="mt-5 rounded-2xl border border-ink/10 bg-paper px-5 py-10 text-center text-sm text-ink/55" data-id="element-2107" data-mp="3clugepbq9hi6">{ordersError}</p>}
          {orders && <NisoloProductSales products={orders.products} totalUnits={orders.units} data-id="element-2108" data-mp="3gllbv6c78lt1" />}
        </> : <>
          <CategoryPerformance selectedOwner={selectedOwner} data-id="element-2109" data-mp="37mra71k0h58m" />
          <SalesBySku selectedOwner={selectedOwner} data-id="element-2110" data-mp="37l6gtf9fnscc" />
        </>}
    </div>;
}
function ChannelMark({
  type
}: {
  type: ChannelPerformance['icon'];
}) {
  const iconSources = {
    manymoons: 'https://cdn.magicpatterns.com/uploads/1H17upTEPhdYPo8CxCujBt/M_manymoons.svg',
    whatnot: 'https://cdn.magicpatterns.com/uploads/tKhY3ZxiXEuMX34dV7VA57/whatnot.svg',
    poshmark: 'https://cdn.magicpatterns.com/uploads/n5jkPZQbazKpxu5cMnVHR4/poshmark.svg',
    prive: 'https://cdn.magicpatterns.com/uploads/fHmVmW1X2zeRckQHVqfLBY/prive.svg',
    shop: 'https://cdn.magicpatterns.com/uploads/geu3g5mDqWsXQVTAfJ1ZvJ/ShopApp.svg'
  } as const;
  if (type === 'shopify') {
    return <span aria-label="Shopify" className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#BB5E40]" data-id="element-2111" data-mp="3i8q2y2hsi2yj">
        <span aria-hidden="true" className="h-4 w-4 bg-cream" style={{
        WebkitMaskImage: 'url(https://cdn.magicpatterns.com/uploads/7LeZM2476qhffRRTPqij7m/shopify_glyph_black.svg)',
        maskImage: 'url(https://cdn.magicpatterns.com/uploads/7LeZM2476qhffRRTPqij7m/shopify_glyph_black.svg)',
        WebkitMaskRepeat: 'no-repeat',
        maskRepeat: 'no-repeat',
        WebkitMaskPosition: 'center',
        maskPosition: 'center',
        WebkitMaskSize: 'contain',
        maskSize: 'contain'
      }} data-id="element-2112" data-mp="3dc9et8albolg" />
      </span>;
  }
  return <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#BB5E40] p-1.5" data-id="element-2113" data-mp="nt7bcl57jw00">
      {/* The Shop wordmark is small within its viewBox, so it is scaled up to match the other marks. */}
      <img src={iconSources[type]} alt="" className={`h-full w-full object-contain ${type === 'shop' ? 'scale-[1.35]' : ''}`} data-id="element-2114" data-mp="qdx3gra2f731" />
    </span>;
}
function SummaryCard({
  icon: Icon,
  label,
  value,
  detail
}: {
  icon: typeof BoxesIcon;
  label: string;
  value: string;
  detail: string;
}) {
  return <article className="rounded-2xl border border-ink/10 bg-paper p-5" data-id="element-2115" data-mp="1532vnvahjx1f">
      <Icon className="h-4 w-4 text-[#BB5E40]" aria-hidden="true" data-id="element-2116" data-mp="154e3q7ayrqyc" />
      <p className="mt-4 text-sm text-ink/55" data-id="element-2117" data-mp="7gcbhbc07se7">{label}</p>
      <p className="mt-1 font-display text-3xl font-semibold tracking-tight text-ink" data-id="element-2118" data-mp="cdchxncrmx5u">{value}</p>
      <p className="mt-1 text-xs text-ink/50" data-id="element-2119" data-mp="hcfojc8e9bfx">{detail}</p>
    </article>;
}
function ChartTooltip({
  active,
  payload,
  label
}: {
  active?: boolean;
  payload?: Array<{
    value?: number;
  }>;
  label?: string;
}) {
  if (!active || !payload?.[0]?.value) return null;
  return <div className="rounded-xl border border-ink/10 bg-paper px-3 py-2 shadow-lg" data-id="element-2120" data-mp="1xr758rw6gu9e">
      <p className="text-xs text-ink/50" data-id="element-2121" data-mp="1xtbfrsol0rin">{label}</p>
      <p className="mt-0.5 text-sm font-semibold tabular-nums text-ink" data-id="element-2122" data-mp="3ttaivvaw09y0">{formatCurrency(payload[0].value)}</p>
    </div>;
}
function formatCurrency(value: number) {
  return value.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  });
}
function formatAxisCurrency(value: number) {
  if (value >= 1000) return `$${Math.round(value / 1000)}k`;
  return `$${value}`;
}